'use client';

import { Button } from '@/shared/components/ui';
import { getTipoEncerramentoConfig } from '@/features/negociacoes';
import { cn } from '@/shared/utils';
import { formatDate } from '@/shared/formatters';

interface ChatBlockedBannerProps {
  tipoEncerramento: string | null;
  dataEncerramento: string | null;
  motivoEncerramento?: string;
  permiteReabrir: boolean;
  janelaExpirada?: boolean;
  onReabrir?: () => void;
}

export function ChatBlockedBanner({
  tipoEncerramento,
  dataEncerramento,
  motivoEncerramento,
  permiteReabrir,
  janelaExpirada = false,
  onReabrir,
}: ChatBlockedBannerProps) {
  const config = getTipoEncerramentoConfig(tipoEncerramento);
  const Icon = config.icon;

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between',
        config.bgClass,
        config.borderClass,
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
            config.iconBgClass,
          )}
        >
          <Icon className={cn('h-5 w-5', config.textClass)} />
        </div>
        <div className="flex-1">
          <p className={cn('font-medium', config.textClass)}>{config.title}</p>
          {(dataEncerramento || motivoEncerramento) && (
            <p className="mt-0.5 text-sm text-neutral-600">
              {dataEncerramento ? `Encerrada em ${formatDate(dataEncerramento)}` : 'Encerrada'}
              {motivoEncerramento && ` • ${motivoEncerramento}`}
            </p>
          )}
          {janelaExpirada && permiteReabrir && (
            <div className="mt-3 rounded-[10px] border border-neutral-200 bg-white/70 px-3 py-2.5 text-xs text-neutral-700">
              <p className="font-semibold">Para voltar a conversar</p>
              <ol className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1.5">
                <li>
                  <span className="text-brand-700 font-semibold">1.</span> Reabra a negociação antes
                  de retomar o contato.
                </li>
                <li>
                  <span className="text-brand-700 font-semibold">2.</span> Se a janela do WhatsApp
                  continuar vencida, envie a mensagem de retomada.
                </li>
                <li>
                  <span className="text-brand-700 font-semibold">3.</span> Aguarde a resposta para
                  escrever livremente.
                </li>
              </ol>
            </div>
          )}
          {!permiteReabrir && (
            <p className="mt-2 text-xs text-neutral-700">
              A reabertura não está disponível neste caso. Consulte o histórico ou a equipe
              responsável antes de retomar o contato.
            </p>
          )}
        </div>
      </div>

      {permiteReabrir && onReabrir && (
        <Button variant="neutral-secondary" size="small" onClick={onReabrir}>
          Reabrir Negociação
        </Button>
      )}
    </div>
  );
}
